import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Flex, Spin } from 'antd';
import { useAuthStore } from '../store/useAuthStore';
import { useCustomerSubscription } from '../hooks/useCustomerSubscription';
import { getDefaultHomePath, isAdmin, isConsultant } from '../utils/isProfile';
import { resolvePlanSignupIntent } from '../utils/planCheckout';
import { snapshotToPostAuthContext } from '../utils/resolvePostAuthPath';
import { getBridgeReturnUrl, saveBridgeReturnUrl } from '../utils/bridgeReturnUrlStorage';
import { sanitizeReturnUrl } from '../utils/urlValidation';
import { handlePostAuthRedirect } from '../utils/handlePostAuthRedirect';

interface PublicRouteProps {
  element: JSX.Element;
}

const FullPageSpin = () => (
  <Flex justify="center" align="center" style={{ height: '100vh' }}>
    <Spin size="large" />
  </Flex>
);

const PublicRoute = ({ element }: PublicRouteProps) => {
  const { authenticated, sessionLoading, loading, user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscription, isLoading: subscriptionLoading } = useCustomerSubscription();
  const hasTriggeredRef = useRef(false);
  const [postAuthPending, setPostAuthPending] = useState(false);

  const isAuthPublicPath = location.pathname === '/login' || location.pathname === '/register';

  useEffect(() => {
    saveBridgeReturnUrl(searchParams.get('return_url'));
  }, [searchParams]);

  useEffect(() => {
    if (!authenticated || !user || sessionLoading || hasTriggeredRef.current) return;
    if (!isAuthPublicPath) return;
    if (subscriptionLoading && !isAdmin(user) && !isConsultant(user)) return;

    const returnUrl = getBridgeReturnUrl() ?? sanitizeReturnUrl(searchParams.get('return_url'));
    if (returnUrl) {
      saveBridgeReturnUrl(returnUrl);
    }

    const intent = resolvePlanSignupIntent(searchParams.get('plan'), searchParams.get('mode'));

    hasTriggeredRef.current = true;
    setPostAuthPending(true);
    void handlePostAuthRedirect({
      user,
      navigate,
      subscription: snapshotToPostAuthContext(subscription),
      returnUrl,
      intent,
    }).finally(() => setPostAuthPending(false));
  }, [
    authenticated,
    user,
    sessionLoading,
    subscriptionLoading,
    subscription,
    isAuthPublicPath,
    searchParams,
    navigate,
  ]);

  if (loading || postAuthPending) {
    return <FullPageSpin />;
  }

  if (!sessionLoading && authenticated && user) {
    if (isAuthPublicPath) {
      if (subscriptionLoading && !isAdmin(user) && !isConsultant(user)) {
        return <FullPageSpin />;
      }
      // Aguarda o useEffect disparar handlePostAuthRedirect (evita flash do formulário).
      return <FullPageSpin />;
    }

    return <Navigate to={getDefaultHomePath(user)} replace />;
  }

  return element;
};

export default PublicRoute;
